import { Injectable } from '@angular/core';
import {
  CompanyFiltersModel,
  MeterRole,
  PartnerModel,
  PartnerResponseModel,
  PartnerUsersResponseModel,
  WhiteLabelUpdateModel,
} from '@core/models';
import { ApiService } from '@core/services/api.service';
import { Observable, map, shareReplay, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  constructor(private readonly apiService: ApiService) {}

  private readonly partnerCache: Record<string, Observable<PartnerModel>> = {};

  // The backend's /partners/{oid:uuid} route only matches lowercase GUIDs; an
  // uppercase oid (as it comes off the JWT-derived user object) falls through
  // to an unrelated legacy route and returns a misleading 422.
  private normalizeOid(partnerOid: string): string {
    return partnerOid.toLowerCase();
  }

  getListCompaniesFilter(filter: CompanyFiltersModel): Observable<PartnerResponseModel> {
    return this.apiService.get('/partners', filter as any);
  }

  getCompanyDetail(partnerOid: string): Observable<PartnerModel> {
    const normalizedOid = this.normalizeOid(partnerOid);
    let cachedPartner = this.partnerCache[normalizedOid];
    if (cachedPartner) {
      return cachedPartner;
    }
    const partner = this.apiService.get(`/partners/${normalizedOid}`).pipe(shareReplay(1));
    this.partnerCache[normalizedOid] = partner;
    return partner;
  }

  updateCompanyLogo(file: any, partnerOid: string): Observable<Blob> {
    const formData = new FormData();

    formData.append('logo', file);
    return this.apiService.put(`/partners/${this.normalizeOid(partnerOid)}/logo`, formData);
  }

  updateWhiteLabel(partnerOid: string, data: WhiteLabelUpdateModel): Observable<Blob> {
    const formData = new FormData();

    if (data.primary_color) {
      formData.append('primary_color', data.primary_color);
    }
    if (data.secondary_color) {
      formData.append('secondary_color', data.secondary_color);
    }
    if (data.open_graph_title) {
      formData.append('open_graph_title', data.open_graph_title);
    }
    if (data.default_logo_uri) {
      formData.append('default_logo_uri', data.default_logo_uri);
    }
    if (data.favicon_logo_uri) {
      formData.append('favicon_logo_url', data.favicon_logo_uri);
    }
    if (data.small_logo_uri) {
      formData.append('small_logo_uri', data.small_logo_uri);
    }

    return this.apiService
      .put(`/partners/${this.normalizeOid(partnerOid)}/updatewhitelabel`, formData)
      .pipe(
        map(response => {
          return response;
        }),
      );
  }

  getUsersByPartner(
    partnerOid: string | undefined,
    offset: number = 0,
    limit: number = 10,
  ): Observable<PartnerUsersResponseModel[]> {
    const payload = {
      limit: limit,
      offset: offset,
    };
    // Omitting partnerOid lets the backend apply its own super-user scope (all companies)
    // instead of filtering to an exact group match — passing XEMEX_OID here would otherwise
    // narrow the result to literal Xemex-staff accounts only.
    const query = partnerOid ? `?partnerOid=${partnerOid}` : '';
    return this.apiService.get(`/users${query}`, payload as any);
  }

  getWhiteLabel(partnerOid: string): Observable<any> {
    return this.apiService.get(`/partners/${this.normalizeOid(partnerOid)}/whitelabel`);
  }

  updateDefaultMeterRole(partnerOid: string, meterRole: MeterRole): Observable<any> {
    const normalizedOid = this.normalizeOid(partnerOid);
    return this.apiService
      .patch(`/partners/${normalizedOid}/default-meter-role`, {
        default_meter_role: meterRole,
      })
      .pipe(
        tap(() => {
          // Invalidate the shareReplay'd getCompanyDetail() cache for this partner so the
          // next read refetches instead of replaying the stale pre-PATCH meter role.
          delete this.partnerCache[normalizedOid];
        }),
      );
  }
}
